import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
  Alert,
  InputBase,
  TableContainer,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
  AddCircleOutlineOutlined,
  DoDisturbOnOutlined,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import Header from "../../common/Header";
import Card from "../../common/Card";
import {
  fetchProviderTemplatesByProvider,
  usePracticeProvidersSlice,
} from "../../../redux/slice/practice/practiceProvidersSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";

interface Incision {
  dx: string;
  depth: string;
  width: string;
  length: string;
  daysPrescribed: string;
  primaryDressing: string;
  ancillaryDressing: string;
  secondaryDressing: string;
  icd10Code: string;
}

interface ProviderTemplate {
  templateId: number;
  providerId: number;
  procedureName: string;
  inactiveDate: string | null;
  templateJson: {
    location: string;
    incisionArray: Incision[];
    procedureName: string;
  };
}

const CollapsibleCard: React.FC<{
  row: ProviderTemplate;
  searchTerm: string;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ row, searchTerm, isExpanded, onToggle }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Highlight matching text
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{ backgroundColor: colors.info[300], color: colors.grey[900] }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.grey[700]}`,
        borderRadius: "10px",
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        px={2}
        py={1.5}
        sx={{
          backgroundColor: colors.grey[900],
          borderBottom: `1px solid ${colors.grey[800]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          transition: "background-color 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: colors.grey[800],
          },
        }}
        onClick={onToggle}
      >
        <Box flex={4}>
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            Procedure Name
          </Typography>
          <Typography fontWeight={600}>
            {highlightText(row.procedureName, searchTerm)}
          </Typography>
        </Box>

        <Box flex={2}>
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            Location
          </Typography>
          <Typography fontWeight={600}>
            {highlightText(row.templateJson?.location || "N/A", searchTerm)}
          </Typography>
        </Box>

        <Box flex={1.5}>
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            Inactive Date
          </Typography>
          <Typography fontWeight={600}>
            {row.inactiveDate
              ? new Date(row.inactiveDate).toLocaleDateString()
              : "N/A"}
          </Typography>
        </Box>

        <Box flex={1.5} textAlign="center">
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            Status
          </Typography>
          <Typography fontWeight={600}>
            <Chip
              label={row.inactiveDate ? "Inactive" : "Active"}
              size="small"
              sx={{
                backgroundColor: !row.inactiveDate ? "#1878418c" : "#872b2296",
                color: "#fff",
                padding: "1px 10px",
                fontWeight: "bold",
                border: "1px solid",
                borderColor: !row.inactiveDate ? "#27ae60" : "#c0392b",
              }}
            />
          </Typography>
        </Box>

        <Box flexShrink={0}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            color="info"
            size="small"
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            backgroundColor: colors.grey[800],
            pt: 2,
            m: 2,
            border: `2px solid ${colors.grey[700]}`,
            borderRadius: 2,
          }}
        >
          {row.templateJson?.incisionArray?.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>DX</TableCell>
                  <TableCell>Depth</TableCell>
                  <TableCell>Width</TableCell>
                  <TableCell>Length</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Secondary</TableCell>
                  <TableCell>Ancillary</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.templateJson.incisionArray.map((incision, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {highlightText(
                        incision.dx || incision.icd10Code || "-",
                        searchTerm
                      )}
                    </TableCell>
                    <TableCell>{incision.depth}</TableCell>
                    <TableCell>{incision.width}</TableCell>
                    <TableCell>{incision.length}</TableCell>
                    <TableCell>{incision.daysPrescribed}</TableCell>
                    <TableCell>
                      {highlightText(incision.primaryDressing, searchTerm)}
                    </TableCell>
                    <TableCell>
                      {highlightText(incision.secondaryDressing, searchTerm)}
                    </TableCell>
                    <TableCell>
                      {highlightText(
                        incision.ancillaryDressing || "-",
                        searchTerm
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                No incision data available for this template.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const ProviderTemplateDetails: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { providerTemplates, getProviderTemplatesStatus } =
    usePracticeProvidersSlice();
  const [searchTerm, setSearchTerm] = useState<string>("");

  const providerId = location.state?.providerId;

  useEffect(() => {
    if (providerId) {
      dispatch(fetchProviderTemplatesByProvider(providerId.toString()));
    }
  }, [dispatch, providerId]);

  const highlightText = (text: string, term: string) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, "gi");
    return text.split(regex).map((part, i) =>
      regex.test(part) ? (
        <span
          key={i}
          style={{ backgroundColor: colors.info[300], color: colors.grey[900] }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const filteredTemplates =
    providerTemplates?.filter((template) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        template.procedureName.toLowerCase().includes(searchLower) ||
        template.templateJson?.location?.toLowerCase().includes(searchLower) ||
        template.templateJson?.incisionArray?.some((incision: any) =>
          [
            incision.dx,
            incision.icd10Code,
            incision.primaryDressing,
            incision.ancillaryDressing,
            incision.secondaryDressing,
          ]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(searchLower))
        )
      );
    }) || [];

  if (getProviderTemplatesStatus === "loading") {
    return (
      <Box sx={{ margin: { xs: "20px 10px", sm: "20px" } }}>
        <Header title="Provider Template List" />
        <Card>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading templates...
            </Typography>
          </Box>
        </Card>
      </Box>
    );
  }

  if (getProviderTemplatesStatus === "error") {
    return (
      <Box sx={{ margin: { xs: "20px 10px", sm: "20px" } }}>
        <Header title="Provider Template List" />
        <Card>
          <Alert severity="error">
            Failed to load Provider Template List. Please try again.
          </Alert>
        </Card>
      </Box>
    );
  }

  if (!providerId) {
    return (
      <Box sx={{ margin: { xs: "20px 10px", sm: "20px" } }}>
        <Header title="Provider Template List" />
        <Card>
          <Alert severity="warning">
            No provider ID provided. Please navigate from the provider list.
          </Alert>
        </Card>
      </Box>
    );
  }

  if (!providerTemplates || providerTemplates.length === 0) {
    return (
      <Box sx={{ margin: { xs: "20px 10px", sm: "20px" } }}>
        <Header title="Provider Template List" />
        <Card>
          <Alert severity="info" sx={{ fontSize: "1.05rem" }}>
            No templates found for this provider.
          </Alert>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: { xs: "20px 10px", sm: "20px" } }}>
      <Header title="Provider Template List" />
      <Card>
        {/* Search Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            backgroundColor: colors.grey[700],
            borderRadius: 3,
            px: 2,
            py: 1,
          }}
        >
          <SearchIcon sx={{ color: colors.grey[400], mr: 1 }} />
          <InputBase
            fullWidth
            placeholder="Search templates by procedure, location, or incision details..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              fontSize: "16px",
              color: colors.grey[100],
              "&::placeholder": {
                color: colors.grey[500],
              },
            }}
          />
        </Box>

        {/* Result Info */}
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {filteredTemplates.length} template(s) matching "{searchTerm}"
          </Typography>
        )}

        {filteredTemplates.length === 0 ? (
          <Alert severity="info">
            No templates found matching your search criteria.
          </Alert>
        ) : (
          <TableContainer
            component={Box}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: colors.grey[900],
              boxShadow: `0 4px 20px ${colors.grey[900]}33`,
               border: `2px solid ${colors.grey[700]}`,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: colors.grey[800],
                    "& th": {
                      color: colors.grey[100],
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.7px",
                      borderBottom: `2px solid ${colors.grey[700]}`,
                    },
                  }}
                >
                  <TableCell>Procedure</TableCell>
                  <TableCell>Location</TableCell>
                  {/* <TableCell>Inactive</TableCell> */}
                  <TableCell>Status</TableCell>
                  <TableCell>DX / ICD10</TableCell>
                  <TableCell>Depth</TableCell>
                  <TableCell>Width</TableCell>
                  <TableCell>Length</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Primary</TableCell>
                  
                  <TableCell>Secondary</TableCell>
                  <TableCell>Ancillary</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredTemplates.map((template) => {
                  const incisions = template.templateJson?.incisionArray || [];

                  return incisions.length > 0 ? (
                    incisions.map((incision: any, index: any) => (
                      <TableRow
                        key={`${template.templateId}-${index}`}
                        sx={{
                          alignItems: "center",
                          backgroundColor:
                            index % 2 === 0
                              ? colors.grey[900]
                              : colors.grey[800],
                          transition: "background 0.3s",
                          "&:hover": {
                            backgroundColor: colors.grey[800],
                          },
                        }}
                      >
                        {index === 0 && (
                          <>
                            <TableCell
                              rowSpan={incisions.length}
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                verticalAlign: "middle",
                              }}
                            >
                              {highlightText(
                                template.procedureName,
                                searchTerm
                              )}
                            </TableCell>

                            <TableCell
                              rowSpan={incisions.length}
                              sx={{
                                fontSize: "0.85rem",
                                verticalAlign: "middle",
                              }}
                            >
                              {highlightText(
                                template.templateJson?.location || "N/A",
                                searchTerm
                              )}
                            </TableCell>

                            {/* <TableCell
                              rowSpan={incisions.length}
                              sx={{
                                fontSize: "0.85rem",
                                verticalAlign: "middle",
                              }}
                            >
                              {template.inactiveDate
                                ? new Date(
                                    template.inactiveDate
                                  ).toLocaleDateString()
                                : "N/A"}
                            </TableCell> */}

                            <TableCell
                              rowSpan={incisions.length}
                              sx={{ verticalAlign: "middle" }}
                            >
                              <Chip
                                label={
                                  template.inactiveDate ? "Inactive" : "Active"
                                }
                                size="small"
                                sx={{
                                  backgroundColor: template.inactiveDate
                                    ? "#ff4d4d22"
                                    : "#27ae6022",
                                  color: template.inactiveDate
                                    ? "#ff4d4d"
                                    : "#2ecc71",
                                  fontWeight: "bold",
                                  px: 1.5,
                                  borderRadius: "999px",
                                  fontSize: "0.75rem",
                                }}
                              />
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {highlightText(
                            incision.dx || incision.icd10Code || "-",
                            searchTerm
                          )}
                        </TableCell>
                        <TableCell>{incision.depth}</TableCell>
                        <TableCell>{incision.width}</TableCell>
                        <TableCell>{incision.length}</TableCell>
                        <TableCell>{incision.daysPrescribed}</TableCell>
                        <TableCell>
                          {highlightText(incision.primaryDressing, searchTerm)}
                        </TableCell>
                        <TableCell>
                          {highlightText(
                            incision.secondaryDressing,
                            searchTerm
                          )}
                        </TableCell>
                        <TableCell>
                          {highlightText(
                            incision.ancillaryDressing || "-",
                            searchTerm
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow
                      key={`empty-${template.templateId}`}
                      sx={{
                        backgroundColor: colors.grey[900],
                        "&:hover": { backgroundColor: colors.grey[800] },
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight={600}>
                          {highlightText(template.procedureName, searchTerm)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {template.templateJson?.location || "N/A"}
                      </TableCell>
                      <TableCell>
                        {template.inactiveDate
                          ? new Date(template.inactiveDate).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            backgroundColor: "#27ae6022",
                            color: "#2ecc71",
                            fontWeight: "bold",
                            borderRadius: "999px",
                          }}
                        />
                      </TableCell>
                      <TableCell colSpan={8}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          No incision data available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};

export default ProviderTemplateDetails;
